// From what the model read to what the engine compares.
//
// The normaliser is deliberately dumb. It trims, validates formats, converts
// units and sums lines — and it never derives one field from another. A subtotal
// that disagrees with its lines is a *finding* for the validation engine, not
// something to quietly repair here. The one exception is `applyCorrections`,
// where a human has typed a day count and the hour count is the same fact in a
// different unit.
//
// Null means "not on the invoice, or not readable" throughout (CINV-8). A sum
// with an unknown term is unknown, never the sum of the known ones.

import type { Currency, RateUnit } from "./constants.js";
import { isValidYmd } from "./dates.js";
import { HOURS_PER_DAY, INVOICE_CONFIDENCE_REVIEW_BELOW, MAX_INVOICE_LINES } from "./rules.js";
import { nameKey } from "./invoice-matching.js";
import type { InvoiceExtraction } from "./invoice-extraction.js";

/**
 * The fields an admin may correct, and the effective values the list sorts and
 * filters on. Kept as a value so the PATCH schema and the corrections overlay
 * enumerate the same set.
 */
export const CORRECTABLE_FIELDS = [
  "invoiceNumber",
  "invoiceDate",
  "description",
  "supplierName",
  "supplierAbn",
  "supplierGstin",
  "currency",
  "periodStart",
  "periodEnd",
  "hoursClaimed",
  "daysClaimed",
  "unitRate",
  "rateUnit",
  "labourAmount",
  "otherCharges",
  "subtotal",
  "taxRatePct",
  "taxAmount",
  "total",
] as const;
export type CorrectableField = (typeof CORRECTABLE_FIELDS)[number];

/** How a field is named to a person — in check text and in the review form. */
export const CORRECTABLE_FIELD_LABEL: Record<CorrectableField, string> = {
  invoiceNumber: "Invoice number",
  invoiceDate: "Invoice date",
  description: "Description",
  supplierName: "Supplier",
  supplierAbn: "ABN",
  supplierGstin: "GSTIN",
  currency: "Currency",
  periodStart: "Period start",
  periodEnd: "Period end",
  hoursClaimed: "Hours claimed",
  daysClaimed: "Days claimed",
  unitRate: "Rate",
  rateUnit: "Rate unit",
  labourAmount: "Work amount",
  otherCharges: "Other charges",
  subtotal: "Subtotal",
  taxRatePct: "Tax rate %",
  taxAmount: "GST / tax",
  total: "Total",
};

export type InvoiceFields = {
  invoiceNumber: string | null;
  /** YYYY-MM-DD. */
  invoiceDate: string | null;
  /** What the invoice says it is for, as printed — never a line's text copied up. */
  description: string | null;
  supplierName: string | null;
  /** Digits only. */
  supplierAbn: string | null;
  supplierGstin: string | null;
  currency: Currency | null;
  /** The service period as printed, YYYY-MM-DD. */
  periodStart: string | null;
  periodEnd: string | null;
  /** Labour hours claimed across every non-expense line. */
  hoursClaimed: number | null;
  /** Labour days claimed — only when every labour line is a DAY line. */
  daysClaimed: number | null;
  /** The one rate, when every labour line shares it. */
  unitRate: number | null;
  rateUnit: RateUnit | null;
  /**
   * What the invoice charges for the work itself: Σ the labour lines. Null if any
   * labour line's amount is unknown, or there are no lines.
   */
  labourAmount: number | null;
  /**
   * Everything billed on top of the work — software, certifications, travel,
   * reimbursements: Σ the expense lines. 0 when a read found none (an empty sum is
   * a fact); null when one has no amount, or nothing was read.
   */
  otherCharges: number | null;
  /** As printed — work and other charges together, before tax. */
  subtotal: number | null;
  taxRatePct: number | null;
  taxAmount: number | null;
  total: number | null;
};

/**
 * An admin's overlay. A key that is present overrides the read value — with a
 * value, or with `null` meaning "this is not on the invoice". A key that is absent
 * leaves the read value alone.
 */
export type InvoiceCorrections = Partial<InvoiceFields>;

export type NormalisedLine = {
  description: string | null;
  quantity: number | null;
  unit: RateUnit | null;
  /** DAY quantity × HOURS_PER_DAY, HOUR quantity as-is, null for OTHER/UNKNOWN. */
  hours: number | null;
  unitRate: number | null;
  amount: number | null;
  projectHint: string | null;
  isExpense: boolean;
};

export type NormalisedInvoice = InvoiceFields & {
  dueDate: string | null;
  supplierEmail: string | null;
  supplierAddress: string | null;
  billToName: string | null;
  taxLabel: string | null;
  paymentReference: string | null;
  bankDetailsPresent: boolean;
  lines: NormalisedLine[];
  /** The weakest section's confidence, 0..1. Null when there was no model read. */
  confidence: number | null;
  /** What the normaliser could not use and why — shown to admins, never a rule. */
  notes: string[];
};

export const roundMoney = (n: number): number => Math.round(n * 100) / 100;

/**
 * The note a read carries when the page stated a currency the portal does not
 * handle. Its currency is null like an unstated one's; this is what tells
 * them apart (EXP-13).
 */
export const UNSUPPORTED_CURRENCY_NOTE = "The invoice is in a currency the portal does not handle.";
export const roundHours = (n: number): number => Math.round(n * 100) / 100;
/**
 * A line's quantity and unit price as printed — 45.372 L at $1.899, 744 h at
 * $0.0116 — never to the cent, or the line no longer multiplies out to its
 * amount (CINV-25). Six places is past any price a document prints; only float
 * noise goes.
 */
export const roundFactor = (n: number): number => Math.round(n * 1e6) / 1e6;

function cleanText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.replace(/\s+/g, " ").trim();
  return trimmed === "" ? null : trimmed;
}

function cleanNumber(
  value: number | null | undefined,
  round: (n: number) => number
): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return round(value);
}

function cleanDate(
  value: string | null | undefined,
  field: string,
  notes: string[]
): string | null {
  const text = cleanText(value);
  if (text === null) return null;
  if (isValidYmd(text)) return text;
  notes.push(`${field} "${text}" is not a YYYY-MM-DD date and was ignored.`);
  return null;
}

/** The lookup key for duplicate detection: case- and punctuation-insensitive. */
export function invoiceNumberKey(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * ABN checksum (the ATO's weighting algorithm). Eleven digits; subtract one from
 * the first, weight each, and the sum is divisible by 89.
 */
export function isValidAbn(digits: string): boolean {
  if (!/^\d{11}$/.test(digits)) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const sum = weights.reduce((acc, weight, i) => {
    const digit = Number(digits[i]) - (i === 0 ? 1 : 0);
    return acc + digit * weight;
  }, 0);
  return sum % 89 === 0;
}

/** The GSTIN shape: state code, PAN, entity number, Z, check character. */
export function looksLikeGstin(value: string): boolean {
  return /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value);
}

const EMPTY_FIELDS: InvoiceFields = {
  invoiceNumber: null,
  invoiceDate: null,
  description: null,
  supplierName: null,
  supplierAbn: null,
  supplierGstin: null,
  currency: null,
  periodStart: null,
  periodEnd: null,
  hoursClaimed: null,
  daysClaimed: null,
  unitRate: null,
  rateUnit: null,
  labourAmount: null,
  otherCharges: null,
  subtotal: null,
  taxRatePct: null,
  taxAmount: null,
  total: null,
};

/**
 * An invoice nobody has read yet: every field null, no lines, no confidence.
 * The starting point when extraction is not configured and an admin types the
 * fields in, and the shape a re-run starts from.
 */
export function emptyInvoice(): NormalisedInvoice {
  return {
    ...EMPTY_FIELDS,
    dueDate: null,
    supplierEmail: null,
    supplierAddress: null,
    billToName: null,
    taxLabel: null,
    paymentReference: null,
    bankDetailsPresent: false,
    lines: [],
    confidence: null,
    notes: [],
  };
}

export function normaliseExtraction(raw: InvoiceExtraction): NormalisedInvoice {
  const notes: string[] = [];

  if (raw.lineItems.length > MAX_INVOICE_LINES) {
    notes.push(
      `The invoice has ${raw.lineItems.length} lines; only the first ${MAX_INVOICE_LINES} were kept, so the totals of the lines will not add up.`
    );
  }
  const lines: NormalisedLine[] = raw.lineItems.slice(0, MAX_INVOICE_LINES).map((line, index) => {
    const quantity = cleanNumber(line.quantity, roundFactor);
    const unit: RateUnit | null =
      line.unit === "DAY" || line.unit === "HOUR" ? line.unit : null;
    const unitRate = cleanNumber(line.unitRate, roundFactor);
    const amount = cleanNumber(line.amount, roundMoney);
    // A credit ("Less: 1 day leave") gives time back, whichever number carries
    // the minus — the printed amount says which way the line runs; without one,
    // quantity × rate does. Its hours count against the claim, so the time and
    // the money on the invoice tell the same story.
    const direction =
      amount !== null && amount !== 0
        ? Math.sign(amount)
        : quantity !== null && unitRate !== null && quantity * unitRate !== 0
          ? Math.sign(quantity * unitRate)
          : Math.sign(quantity ?? 1) || 1;
    let hours: number | null = null;
    if (quantity !== null && (unit === "DAY" || unit === "HOUR")) {
      const magnitude = Math.abs(quantity) * (unit === "DAY" ? HOURS_PER_DAY : 1);
      hours = roundHours(direction < 0 ? -magnitude : magnitude);
      if (direction < 0) {
        notes.push(
          `Line "${cleanText(line.description) ?? `#${index + 1}`}" is a credit of ${Math.abs(quantity)} ${unit === "DAY" ? "day" : "hour"}${Math.abs(quantity) === 1 ? "" : "s"}, taken off the time claimed.`
        );
      }
    }
    return {
      description: cleanText(line.description),
      quantity,
      unit,
      hours,
      unitRate,
      amount,
      projectHint: cleanText(line.projectOrClientHint),
      isExpense: line.isExpense,
    };
  });

  const labour = lines.filter((line) => !line.isExpense);
  const expenses = lines.filter((line) => line.isExpense);

  // Σ labour hours — but a single unknown line poisons the total. Summing the
  // known ones would claim the contractor invoiced fewer hours than they did.
  let hoursClaimed: number | null = null;
  if (labour.length > 0) {
    const unknown = labour.findIndex((line) => line.hours === null);
    if (unknown === -1) {
      hoursClaimed = roundHours(labour.reduce((sum, line) => sum + (line.hours ?? 0), 0));
    } else {
      const line = labour[unknown]!;
      notes.push(
        `Hours could not be totalled: line "${line.description ?? `#${lines.indexOf(line) + 1}`}" has ${
          line.unit === null ? "no recognisable unit" : "no quantity"
        }.`
      );
    }
  }

  // Days are only a claim when the invoice is *in* days throughout. A mixed
  // invoice renders as days from its hours in the engine and stores none here.
  // Summed from the signed hours, not the quantities, so a credit takes its day
  // off exactly as it takes its hours off — one fact in two units.
  const daysClaimed =
    labour.length > 0 &&
    labour.every((line) => line.unit === "DAY" && line.hours !== null)
      ? roundHours(
          labour.reduce((sum, line) => sum + (line.hours ?? 0), 0) / HOURS_PER_DAY
        )
      : null;

  // One rate when every labour line agrees; otherwise the lines carry them.
  const rateKeys = new Set(
    labour.map((line) =>
      line.unitRate === null || line.unit === null ? "?" : `${line.unitRate}:${line.unit}`
    )
  );
  const soleRate =
    labour.length > 0 && rateKeys.size === 1 && !rateKeys.has("?") ? labour[0]! : null;

  const sumAmounts = (subset: NormalisedLine[]): number | null =>
    subset.some((line) => line.amount === null)
      ? null
      : roundMoney(subset.reduce((sum, line) => sum + (line.amount ?? 0), 0));

  const currency: Currency | null =
    raw.currency === "AUD" || raw.currency === "INR" ? raw.currency : null;
  if (raw.currency === "OTHER") notes.push(UNSUPPORTED_CURRENCY_NOTE);

  const abnDigits = cleanText(raw.supplier.abn)?.replace(/\D/g, "") ?? null;
  const gstin = cleanText(raw.supplier.gstin)?.toUpperCase().replace(/\s+/g, "") ?? null;

  const scores = [
    raw.confidence.document,
    raw.confidence.supplier,
    raw.confidence.header,
    raw.confidence.lineItems,
    raw.confidence.totals,
  ].map((score) => (Number.isFinite(score) ? Math.min(1, Math.max(0, score)) : 0));

  let periodStart = cleanDate(raw.periodStart, "periodStart", notes);
  let periodEnd = cleanDate(raw.periodEnd, "periodEnd", notes);
  if (periodStart && periodEnd && periodStart > periodEnd) {
    notes.push(
      `The period ends (${periodEnd}) before it starts (${periodStart}); both were ignored.`
    );
    periodStart = null;
    periodEnd = null;
  }

  return {
    invoiceNumber: cleanText(raw.invoiceNumber),
    invoiceDate: cleanDate(raw.invoiceDate, "invoiceDate", notes),
    description: cleanText(raw.description),
    dueDate: cleanDate(raw.dueDate, "dueDate", notes),
    supplierName: cleanText(raw.supplier.name),
    supplierAbn: abnDigits === "" ? null : abnDigits,
    supplierGstin: gstin === "" ? null : gstin,
    supplierEmail: cleanText(raw.supplier.email)?.toLowerCase() ?? null,
    supplierAddress: cleanText(raw.supplier.address),
    billToName: cleanText(raw.billToName),
    currency,
    periodStart,
    periodEnd,
    hoursClaimed,
    daysClaimed,
    // The contractor's rate, to the cent like every money column; the line keeps its own.
    unitRate: soleRate?.unitRate == null ? null : roundMoney(soleRate.unitRate),
    rateUnit: soleRate?.unit ?? null,
    labourAmount: labour.length > 0 ? sumAmounts(labour) : null,
    otherCharges: expenses.length > 0 ? sumAmounts(expenses) : 0,
    subtotal: cleanNumber(raw.subtotal, roundMoney),
    taxLabel: cleanText(raw.taxLabel),
    taxRatePct: cleanNumber(raw.taxRatePct, roundMoney),
    taxAmount: cleanNumber(raw.taxAmount, roundMoney),
    total: cleanNumber(raw.total, roundMoney),
    paymentReference: cleanText(raw.paymentReference),
    bankDetailsPresent: raw.bankDetailsPresent,
    lines,
    confidence: Math.min(...scores),
    notes,
  };
}

/**
 * A stored read as it is today. Reads stored before engine v4 carry the two line
 * sums as `labourSubtotal` / `expenseTotal`, which are now the correctable
 * `labourAmount` / `otherCharges`; the rest is filled from `emptyInvoice()`.
 */
export function upgradeStoredInvoice(
  stored: Partial<NormalisedInvoice> & {
    labourSubtotal?: number | null;
    expenseTotal?: number | null;
  }
): NormalisedInvoice {
  const { labourSubtotal, expenseTotal, ...rest } = stored;
  return {
    ...emptyInvoice(),
    ...(labourSubtotal !== undefined ? { labourAmount: labourSubtotal } : {}),
    ...(expenseTotal !== undefined ? { otherCharges: expenseTotal } : {}),
    ...rest,
  };
}

/**
 * The key a vendor is grouped by (EXP-3): the supplier name as `nameKey`
 * normalises it, for "Snowmicro Pty Ltd" and "SNOWMICRO" are one vendor.
 *
 * Deliberately its own name, not `nameKey` itself. `nameKey` answers
 * "plausibly the same party?", where a false yes costs one review row; this
 * answers "the same vendor, for spend", where a false merge sums two vendors
 * into one figure. Changing either must not silently re-partition the other —
 * and changing this one means backfilling `supplierKey`.
 */
export function supplierKeyOf(name: string | null): string | null {
  if (name === null) return null;
  // A name made only of entity words ("The Co") strips to nothing, and an empty
  // key would merge every such vendor into one bucket — so keep the words then.
  const stripped = nameKey(name);
  if (stripped) return stripped.slice(0, 200);
  const raw = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return raw ? raw.slice(0, 200) : null;
}

/** Just the correctable/effective fields of an invoice. */
export function invoiceFieldsOf(invoice: InvoiceFields): InvoiceFields {
  return Object.fromEntries(
    CORRECTABLE_FIELDS.map((field) => [field, invoice[field]])
  ) as InvoiceFields;
}

/**
 * The read invoice with an admin's corrections laid over it.
 *
 * The **only** cross-field derivation in this module, and only because a human
 * did the typing: days and hours are one fact in two units (RATE-3), so a
 * corrected day count implies the hours, and corrected hours on a day-rated
 * invoice imply the days. Everything else stays exactly as corrected.
 */
export function applyCorrections(
  base: NormalisedInvoice,
  corrections: InvoiceCorrections
): { invoice: NormalisedInvoice; correctedFields: CorrectableField[] } {
  const correctedFields = CORRECTABLE_FIELDS.filter((field) => field in corrections);
  const invoice: NormalisedInvoice = { ...base };
  for (const field of correctedFields) {
    // The overlay is keyed by the same field names, so the assignment is exact;
    // the cast is only because TypeScript cannot correlate the key and the value.
    (invoice as Record<CorrectableField, unknown>)[field] = corrections[field] ?? null;
  }

  const daysCorrected = correctedFields.includes("daysClaimed");
  const hoursCorrected = correctedFields.includes("hoursClaimed");
  if (daysCorrected && !hoursCorrected && invoice.daysClaimed !== null) {
    invoice.hoursClaimed = roundHours(invoice.daysClaimed * HOURS_PER_DAY);
  } else if (
    hoursCorrected &&
    !daysCorrected &&
    invoice.hoursClaimed !== null &&
    invoice.rateUnit === "DAY"
  ) {
    invoice.daysClaimed = roundHours(invoice.hoursClaimed / HOURS_PER_DAY);
  }

  return { invoice, correctedFields };
}

// --- Reading again (CINV-31) --------------------------------------------------

export const ESCALATION_REASONS = [
  "LOW_CONFIDENCE",
  "NO_TOTAL",
  "NO_INVOICE_NUMBER",
  "NO_HOURS",
] as const;
export type EscalationReason = (typeof ESCALATION_REASONS)[number];

/**
 * Why a read is worth paying a stronger model to read again: the model was
 * unsure, or a figure every invoice carries came back empty. A document that is
 * not an invoice is not read again — a quote is a quote to both models. Hours
 * count only when there is labour to have hours: a fixed-price invoice has none.
 * The period is left out on purpose: plenty of invoices print none.
 */
export function escalationReasons(
  normalised: NormalisedInvoice,
  reply: Pick<InvoiceExtraction, "isInvoice" | "documentKind">
): EscalationReason[] {
  if (reply.isInvoice === false) return [];
  if (reply.documentKind !== "INVOICE" && reply.documentKind !== "UNKNOWN") return [];
  const reasons: EscalationReason[] = [];
  if (normalised.confidence === null || normalised.confidence < INVOICE_CONFIDENCE_REVIEW_BELOW)
    reasons.push("LOW_CONFIDENCE");
  if (normalised.total === null) reasons.push("NO_TOTAL");
  if (normalised.invoiceNumber === null) reasons.push("NO_INVOICE_NUMBER");
  const labour = normalised.lines.filter((line) => !line.isExpense);
  if (labour.length > 0 && normalised.hoursClaimed === null && normalised.daysClaimed === null)
    reasons.push("NO_HOURS");
  return reasons;
}
